
import React, { useEffect, useRef } from 'react';
import { useLanguage } from '@/context/LanguageContext';

const TradersSection: React.FC = () => {
  const { t } = useLanguage();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-fast');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const animateElements = document.querySelectorAll('.animate-on-scroll');
    animateElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      animateElements.forEach((element) => {
        observer.unobserve(element);
      });
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-20 relative bg-gradient-to-b from-gray-900 to-indigo-950/30">
      {/* Background elements */}
      <div className="absolute inset-0 bg-cover bg-center opacity-5"></div>
      <div className="absolute top-0 left-1/3 w-96 h-96 rounded-full bg-purple-500/5 blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-2 text-white animate-on-scroll will-change-transform">
            {t('traders.header')}
          </h2>
          
          <p className="text-3xl md:text-3x1 text-purple-400 mb-6 animate-on-scroll will-change-transform">
            {t('traders.subtitle')}
          </p>
          
          <p className="text-lg text-gray-300 mb-12 leading-relaxed animate-on-scroll will-change-transform">
            {t('traders.copy')}
          </p>
          
          {/* Trader statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 animate-on-scroll will-change-transform">
              <div className="text-4xl font-bold text-purple-400 mb-2">{t('traders.stats.experience')}</div>
              <div className="text-gray-300">{t('traders.stats.experienceLabel')}</div>
            </div>
            
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 animate-on-scroll animate-delay-50 will-change-transform">
              <div className="text-4xl font-bold text-purple-400 mb-2">{t('traders.stats.rank')}</div>
              <div className="text-gray-300">{t('traders.stats.rankLabel')}</div>
            </div>
            
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 animate-on-scroll animate-delay-100 will-change-transform">
              <div className="text-4xl font-bold text-purple-400 mb-2">{t('traders.stats.since')}</div>
              <div className="text-gray-300">{t('traders.stats.sinceLabel')}</div>
            </div>
          </div>
          
          {/* Trading approaches */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-left animate-on-scroll will-change-transform">
              <h3 className="text-xl font-semibold mb-4 text-white">{t('traders.approach.algorithmic.title')}</h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-purple-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>{t('traders.approach.algorithmic.point1')}</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-purple-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>{t('traders.approach.algorithmic.point2')}</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-purple-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>{t('traders.approach.algorithmic.point3')}</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-left animate-on-scroll animate-delay-50 will-change-transform">
              <h3 className="text-xl font-semibold mb-4 text-white">{t('traders.approach.risk.title')}</h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-purple-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>{t('traders.approach.risk.point1')}</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-purple-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>{t('traders.approach.risk.point2')}</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-purple-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>{t('traders.approach.risk.point3')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TradersSection;
